import { useState, useEffect, useRef } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import communityAPI from "../api/community";
import LegalDocumentModal from "./community/LegalDocumentModal";

export default function GoogleLoginButton({ onSuccess, onError }) {
  const navigate = useNavigate();
  const buttonWrapRef = useRef(null);
  const [gsiButtonWidth, setGsiButtonWidth] = useState(undefined);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [pendingCredential, setPendingCredential] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [pendingTermsModal, setPendingTermsModal] = useState(false);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });

  useEffect(() => {
    // @react-oauth/google expects GSI button `width` as pixels, not "100%".
    // We measure our wrapper and pass a numeric pixel width for consistent rendering.
    const el = buttonWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const updateWidth = () => {
      const width = el.getBoundingClientRect().width;
      setGsiButtonWidth(width > 0 ? Math.floor(width) : undefined);
    };

    updateWidth();

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!legalModal.isOpen && pendingTermsModal) {
      setTermsModalOpen(true);
      setPendingTermsModal(false);
    }
  }, [legalModal.isOpen, pendingTermsModal]);

  const handleSuccess = async (credentialResponse) => {
    try {
      const res = await communityAPI.auth.googleLogin(
        credentialResponse.credential,
        false
      );
      if (res.data.terms_required) {
        setUserInfo(res.data.user_info);
        setPendingCredential(credentialResponse.credential);
        setTermsModalOpen(true);
        setAgreedToTerms(false);
      } else {
        onSuccess?.(res.data.user);
      }
    } catch (err) {
      onError?.(err.response?.data?.error || "Google login failed");
    }
  };

  const handleTermsAccept = async () => {
    if (!agreedToTerms) {
      onError?.("Please accept the Terms and Privacy Policy");
      return;
    }
    try {
      const res = await communityAPI.auth.googleLogin(pendingCredential, true);
      setTermsModalOpen(false);
      setUserInfo(null);
      setPendingCredential(null);
      setAgreedToTerms(false);
      
      // Show welcome modal for new users
      setNewUser(res.data.user);
      setWelcomeModalOpen(true);
    } catch (err) {
      onError?.(err.response?.data?.error || "Google login failed");
    }
  };

  const handleWelcomeComplete = () => {
    setWelcomeModalOpen(false);
    onSuccess?.(newUser);
    setNewUser(null);
    navigate("/community/profile/edit");
  };

  return (
    <>
      <div ref={buttonWrapRef} className="w-full">
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={() => onError?.("Failed to connect with Google")}
          useOneTap={false}
          theme="outline"
          size="large"
          text="continue_with"
          width={gsiButtonWidth}
        />
      </div>

      <Modal
        open={termsModalOpen}
        onCancel={() => {
          setTermsModalOpen(false);
          setUserInfo(null);
          setPendingCredential(null);
          setAgreedToTerms(false);
        }}
        footer={null}
        centered
        className="max-w-md"
      >
        {/* Header */}
        {userInfo && (
          <div className="mb-6 text-center">
            {userInfo.picture && (
              <div className="mx-auto mb-3 h-16 w-16 rounded-full ring-4 ring-primary/20">
                <img
                  src={userInfo.picture}
                  alt="Profile"
                  className="h-full w-full rounded-full object-cover"
                />
              </div>
            )}
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Welcome, {userInfo.name || userInfo.email}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Complete your account setup to continue
            </p>
          </div>
        )}

        {/* Terms Box */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
          <label
            htmlFor="google-terms-modal"
            className="flex cursor-pointer items-start gap-3"
          >
            <input
              type="checkbox"
              id="google-terms-modal"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              I agree to the{" "}
              <button
                type="button"
                onClick={() => {
                  setTermsModalOpen(false);
                  setPendingTermsModal(true);
                  setLegalModal({ isOpen: true, type: "terms" });
                }}
                className="font-medium text-primary hover:underline"
              >
                Terms of Service
              </button>{" "}
              and{" "}
              <button
                type="button"
                onClick={() => {
                  setTermsModalOpen(false);
                  setPendingTermsModal(true);
                  setLegalModal({ isOpen: true, type: "privacy" });
                }}
                className="font-medium text-primary hover:underline"
              >
                Privacy Policy
              </button>
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => {
              setTermsModalOpen(false);
              setUserInfo(null);
              setPendingCredential(null);
              setAgreedToTerms(false);
            }}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>

          <button
            onClick={handleTermsAccept}
            disabled={!agreedToTerms}
            className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Agree & Continue
          </button>
        </div>
      </Modal>

      <LegalDocumentModal
        type={legalModal.type}
        isOpen={legalModal.isOpen}
        onClose={() => setLegalModal({ isOpen: false, type: null })}
      />

      {/* Welcome Modal */}
      <Modal
        open={welcomeModalOpen}
        onCancel={handleWelcomeComplete}
        footer={null}
        centered
        className="max-w-lg"
      >
        <div className="py-6">
          {/* Success Icon */}
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <svg
              className="h-10 w-10 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Welcome Message */}
          <div className="mb-6 text-center">
            <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
              Welcome to Our Community! 🎉
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your account has been successfully created
            </p>
          </div>

          {/* Next Steps */}
          <div className="mb-6 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-5 dark:border-primary/30 dark:from-primary/10 dark:to-primary/20">
            <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <svg className="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Complete Your Profile to Get Started
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                  ✓
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Add Your Professional Information
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Bio, title, location, and skills to showcase your expertise
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                  ✓
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Customize Your Profile
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Upload a cover image and personalize your profile page
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                  ✓
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Connect with the Community
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Share your contact info to network with other professionals
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setWelcomeModalOpen(false);
                onSuccess?.(newUser);
                setNewUser(null);
              }}
              className="flex-1 rounded-lg border border-gray-300 bg-white py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Skip for Now
            </button>
            <button
              onClick={handleWelcomeComplete}
              className="flex-1 rounded-lg bg-primary py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
            >
              Complete Profile
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
